import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { SkyLoader } from "@skypointcloud/skypoint-ui";
import {
  useGetWorkforcePackQuery,
  useInstallPackMutation,
  useResetPackMutation,
  useCreateDepartmentMutation,
  useUpdateDepartmentMutation,
  useDeleteDepartmentMutation,
  useAddDepartmentRoleMutation,
  useDeleteDepartmentRoleMutation,
  useCreateAssetTypeMutation,
  useUpdateAssetTypeMutation,
  useDeleteAssetTypeMutation,
  useAddAssetTypeAttributeMutation,
  useDeleteAssetTypeAttributeMutation,
  type WlmAttributeType,
  type WlmDepartment,
  type WlmAssetType,
  type WlmSodMode,
} from "../../../store/api/workforceApi";
import { getSectorPack, type SectorId, type SectorPack } from "./sectorPacks";
import { extractWorkforceError } from "../ui/errors";

// Same value as ROUTES.WORKFORCE_SETUP (added with the setup route); kept as a
// local const so this module has no dependency on the route registration.
const SETUP_PATH = "/workforce/setup";

export interface WorkforcePackState {
  sectorId: SectorId;
  customized: boolean;
  departments: {
    id: string;
    name: string;
    description: string;
    roles: string[];
    memberCount: number;
  }[];
  assetTypes: { id: string; name: string; attributes: string[] }[];
}

interface WorkforcePackContextValue {
  packState: WorkforcePackState | null;
  pack: SectorPack | null;
  isLoading: boolean;
  installError: string | null;
  /** Portal configured to receive onboarding/offboarding lifecycle tickets, or null if unset. */
  opsPortalId: string | null;
  /** Wave 1 execution toggles — see WorkforcePackInstall. False (and thus
   * ticket-only) until a pack is installed. */
  autoProvisionAccess: boolean;
  autoSeats: boolean;
  autoAssets: boolean;
  /** Wave 2 approval gate — when on, lifecycle runs defer execution to an
   * admin approve/reject decision instead of running inline. Off by default. */
  requireApproval: boolean;
  /** Wave 2 account lifecycle toggle — when on, offboarding disables the
   * user's Entra account and onboarding re-enables it. Off by default. */
  autoDisableAccounts: boolean;
  /** SoD enforcement mode (2e) — "warn" audits + allows a conflicting role
   * assignment, "block" rejects it. "off" by default. */
  sodMode: WlmSodMode;
  /** Entra manager write-back toggle — when on, changing a person's manager
   * here also updates their manager in Microsoft Entra. Off by default. */
  syncManagerToEntra: boolean;
  /**
   * Unmapped query departments (id/name/description/roles/onboardingTasks in
   * their full server shape) — for components that need fields packState
   * intentionally drops (e.g. onboardingTasks) without reshaping packState
   * itself. Empty array until the pack query resolves.
   */
  departmentsRaw: WlmDepartment[];
  /**
   * Unmapped query asset types (full typed attribute defs — id, name,
   * attributeType, sortOrder) — mirrors departmentsRaw for components that
   * need the typed attribute list packState intentionally flattens to
   * string[] (e.g. AssetTypesPage's AttributeList, AddAssetForm's typed
   * inputs). Empty array until the pack query resolves.
   */
  assetTypesRaw: WlmAssetType[];
  selectSector: (id: SectorId) => void;
  resetToPack: () => void;
  addDepartment: (name: string) => void;
  renameDepartment: (id: string, name: string) => void;
  removeDepartment: (id: string) => void;
  addRole: (deptId: string, role: string) => void;
  removeRole: (deptId: string, role: string) => void;
  addAssetType: (name: string) => void;
  renameAssetType: (id: string, name: string) => void;
  removeAssetType: (id: string) => void;
  addAttribute: (typeId: string, attribute: string, attributeType?: WlmAttributeType) => void;
  removeAttribute: (typeId: string, attribute: string) => void;
}

const WorkforcePackContext = createContext<WorkforcePackContextValue | null>(null);

/**
 * Adapter over the workforce API: fetches the installed pack (departments,
 * roles, asset types, attributes) and exposes the same component-facing
 * shape/mutator names the prior browser-only working-copy context used to,
 * so pages don't need to change. Every mutator fires the matching
 * workforceApi mutation directly — the RTK Query cache (invalidated per
 * mutation) is the source of truth, not local component state.
 */
export function WorkforcePackProvider({ children }: { children: ReactNode }) {
  const { data, isLoading } = useGetWorkforcePackQuery();
  const [installPack] = useInstallPackMutation();
  const [resetPackMutation] = useResetPackMutation();
  const [createDepartment] = useCreateDepartmentMutation();
  const [updateDepartment] = useUpdateDepartmentMutation();
  const [deleteDepartment] = useDeleteDepartmentMutation();
  const [addDepartmentRole] = useAddDepartmentRoleMutation();
  const [deleteDepartmentRole] = useDeleteDepartmentRoleMutation();
  const [createAssetType] = useCreateAssetTypeMutation();
  const [updateAssetType] = useUpdateAssetTypeMutation();
  const [deleteAssetType] = useDeleteAssetTypeMutation();
  const [addAssetTypeAttribute] = useAddAssetTypeAttributeMutation();
  const [deleteAssetTypeAttribute] = useDeleteAssetTypeAttributeMutation();

  const [installError, setInstallError] = useState<string | null>(null);

  const packState: WorkforcePackState | null = useMemo(() => {
    if (!data?.install) return null;
    return {
      sectorId: data.install.sectorId as SectorId,
      customized: data.install.customized,
      departments: data.departments.map((d) => ({
        id: d.id,
        name: d.name,
        description: d.description,
        roles: d.roles.map((r) => r.name),
        memberCount: d.memberCount,
      })),
      assetTypes: data.assetTypes.map((t) => ({
        id: t.id,
        name: t.name,
        attributes: t.attributes.map((a) => a.name),
      })),
    };
  }, [data]);

  const selectSector = (sectorId: SectorId) => {
    setInstallError(null);
    installPack({ sectorId })
      .unwrap()
      .catch((error: unknown) =>
        setInstallError(extractWorkforceError(error, "Failed to install sector pack")),
      );
  };

  const resetToPack = () => {
    setInstallError(null);
    resetPackMutation()
      .unwrap()
      .catch((error: unknown) =>
        setInstallError(extractWorkforceError(error, "Failed to reset pack")),
      );
  };

  const addDepartment = (name: string) => {
    void createDepartment({ name });
  };

  const renameDepartment = (id: string, name: string) => {
    void updateDepartment({ id, body: { name } });
  };

  const removeDepartment = (id: string) => {
    void deleteDepartment(id);
  };

  const addRole = (deptId: string, role: string) => {
    void addDepartmentRole({ departmentId: deptId, name: role });
  };

  // Pages work with role/attribute names (string[]), but deletion endpoints
  // are id-keyed — resolve the id from the raw query payload here. Unknown
  // names (already removed / stale render) no-op instead of erroring.
  const removeRole = (deptId: string, roleName: string) => {
    const role = data?.departments
      .find((d) => d.id === deptId)
      ?.roles.find((r) => r.name === roleName);
    if (role) void deleteDepartmentRole({ departmentId: deptId, roleId: role.id });
  };

  const addAssetType = (name: string) => {
    void createAssetType({ name });
  };

  const renameAssetType = (id: string, name: string) => {
    void updateAssetType({ id, name });
  };

  const removeAssetType = (id: string) => {
    void deleteAssetType(id);
  };

  const addAttribute = (
    typeId: string,
    attribute: string,
    attributeType: WlmAttributeType = "text",
  ) => {
    void addAssetTypeAttribute({ assetTypeId: typeId, name: attribute, attributeType });
  };

  const removeAttribute = (typeId: string, attributeName: string) => {
    const attribute = data?.assetTypes
      .find((t) => t.id === typeId)
      ?.attributes.find((a) => a.name === attributeName);
    if (attribute) {
      void deleteAssetTypeAttribute({ assetTypeId: typeId, attrId: attribute.id });
    }
  };

  const value: WorkforcePackContextValue = {
    packState,
    pack: packState ? getSectorPack(packState.sectorId) : null,
    isLoading,
    installError,
    opsPortalId: data?.install?.opsPortalId ?? null,
    autoProvisionAccess: data?.install?.autoProvisionAccess ?? false,
    autoSeats: data?.install?.autoSeats ?? false,
    autoAssets: data?.install?.autoAssets ?? false,
    requireApproval: data?.install?.requireApproval ?? false,
    autoDisableAccounts: data?.install?.autoDisableAccounts ?? false,
    sodMode: data?.install?.sodMode ?? "off",
    syncManagerToEntra: data?.install?.syncManagerToEntra ?? false,
    departmentsRaw: data?.departments ?? [],
    assetTypesRaw: data?.assetTypes ?? [],
    selectSector,
    resetToPack,
    addDepartment,
    renameDepartment,
    removeDepartment,
    addRole,
    removeRole,
    addAssetType,
    renameAssetType,
    removeAssetType,
    addAttribute,
    removeAttribute,
  };

  return <WorkforcePackContext.Provider value={value}>{children}</WorkforcePackContext.Provider>;
}

export function useWorkforcePack(): WorkforcePackContextValue {
  const context = useContext(WorkforcePackContext);
  if (!context) {
    throw new Error("useWorkforcePack must be used within a WorkforcePackProvider");
  }
  return context;
}

/**
 * Same as useWorkforcePack, but returns null instead of throwing when
 * there's no WorkforcePackProvider ancestor — for components that can be
 * mounted either inside the /workforce/* route tree or standalone with
 * their pack data threaded in as props instead (e.g. OnboardingDialog from
 * the ticket sidebar panel).
 */
export function useWorkforcePackOptional(): WorkforcePackContextValue | null {
  return useContext(WorkforcePackContext);
}

/**
 * Redirects to the sector-selection wizard until both a sector pack has been
 * chosen AND an ops portal is connected to receive lifecycle tickets.
 * Mounted inside WorkforcePackProvider, wrapping the WLM route tree. Shows a
 * centered loader while the pack query is in flight so a not-yet-loaded
 * pack doesn't flash a redirect to setup before the real state arrives.
 */
export function PackGate({ children }: { children: ReactNode }) {
  const { packState, opsPortalId, isLoading } = useWorkforcePack();
  const location = useLocation();
  const isSetup = location.pathname.startsWith(SETUP_PATH);

  if (isLoading) {
    // Match the app-wide route Suspense fallback (AppLayout) exactly, so the
    // lazy-chunk loader and this pack-query loader read as one continuous
    // load instead of two different-looking spinners in sequence.
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <SkyLoader label="Loading..." />
      </div>
    );
  }

  if ((!packState || opsPortalId === null) && !isSetup) {
    return (
      <Navigate
        to={SETUP_PATH + location.search}
        replace
      />
    );
  }
  return <>{children}</>;
}
